import type {
  ContentType,
  SinglePagePdf,
  VirtualFile,
} from "@pdfking/pdfengine"
import type { AnyGUID } from "@pdfking/tfunc"
import { AtomHash, UUID } from "@pdfking/tfunc"
import {
  Identity,
  IterableToArray,
  MapAsync,
  MapAsyncConcurrent,
} from "@pdfking/tfunc"
import first from "rfuncs/functions/first"
import key_take from "rfuncs/functions/key_take"
import keys from "rfuncs/functions/keys"
import length from "rfuncs/functions/length"
import object_map from "rfuncs/functions/object_map"
import ordered_object from "rfuncs/functions/ordered_object"
import range from "rfuncs/functions/range"
import scan from "rfuncs/functions/scan"
import values from "rfuncs/functions/values"
import zip from "rfuncs/functions/zip"

import merge_pdf from "./merge_pdf"
import { CreateRemoteFile } from "./pdfengine/Functions"
import { PlotPdf } from "./pdfengine/Functions"
import plot_imposition_pdf_runner from "./plot/plot_imposition_pdf"
import plot_media_pdf_runner from "./plot/plot_media_pdf"
import split_pdf from "./split_pdf"
import expand_page_groups from "./utils/expand_page_groups"
import type { DispatchData } from "./utils/get_imposition_dispatch_data"
import get_imposition_dispatch_data from "./utils/get_imposition_dispatch_data"

const selector = s => [...document.querySelectorAll(s)]

const collect_file_info = async (
  source: Promise<VirtualFile>
): Promise<Record<number, SinglePagePdf>> =>
  IterableToArray(
    MapAsync(f => [f.page_number, f], split_pdf(await source))
  ).then(Object.fromEntries)

const dispatch_imposition = async (
  hash_id: AnyGUID,
  runner: any,
  { url, file_data, destinations }: DispatchData,
  filenames: Record<string, Promise<VirtualFile>>,
  destination: string,
  spec: any,
  layers: any
): Promise<VirtualFile> => {
  let used_pages =
    expand_page_groups(file_data, key_take(destinations, destination))[
      destination
    ] || {}
  let used_filenames: Record<string, Promise<VirtualFile>> = {}
  scan(
    ({ filename }) => (used_filenames[filename] = filenames[filename]),
    used_pages
  )

  const info = await Promise.all(values(used_filenames).map(collect_file_info))
  used_filenames = ordered_object(zip(keys(used_filenames), info))
  scan(
    ({ page, filename, scale }, k) =>
      (used_pages[k] = {
        scale,
        file: used_filenames[filename][page],
        page: 1,
      }),
    used_pages
  )
  const printable = length(filenames) > 0

  const pages = MapAsyncConcurrent(
    async ({
      plot,
      context,
      page,
      total,
    }): Promise<[number, number, SinglePagePdf]> => [
      page,
      total,
      await PlotPdf(plot, context, await AtomHash([page, hash_id])),
    ],
    runner([[url, used_pages, spec, printable, layers]])
  )

  return merge_pdf(pages, hash_id)
}
const download = async (
  hash_id: AnyGUID,
  _action: DispatchData,
  filenames: Record<string, Promise<VirtualFile>>,
  url: string,
  type: ContentType
): Promise<VirtualFile> => CreateRemoteFile({ mirrors: [url], type })
const download_selector = async (
  hash_id: AnyGUID,
  args: DispatchData,
  filenames: Record<string, Promise<VirtualFile>>,
  selection: string,
  type: ContentType
) =>
  download(
    hash_id,
    args,
    filenames,
    first(selector(selection)).getAttribute("href"),
    type
  )

const plot_imposition_pdf = async (
  hash_id: AnyGUID,
  info: DispatchData,
  filenames: Record<string, Promise<VirtualFile>>,
  destination: any,
  spec: any,
  layers: any
): Promise<VirtualFile> =>
  dispatch_imposition(
    hash_id,
    plot_imposition_pdf_runner,
    info,
    filenames,
    destination,
    spec,
    layers
  )

const plot_media_pdf = async (
  hash_id: AnyGUID,
  info: DispatchData,
  filenames: Record<string, Promise<VirtualFile>>,
  destination: any,
  spec: any
): Promise<VirtualFile> =>
  dispatch_imposition(
    hash_id,
    plot_media_pdf_runner,
    info,
    filenames,
    destination,
    object_map(
      (p, i) => i + 1,
      Identity,
      spec || range(1, info.total_media_pages)
    ),
    []
  )

const actions: Record<
  string,
  (
    hash_id: AnyGUID,
    action: DispatchData,
    filenames: Record<string, Promise<VirtualFile>>,
    ...args: any[]
  ) => Promise<VirtualFile>
> = {
  plot_imposition_pdf,
  plot_media_pdf,
  download,
  download_selector,
}
export default async ({
  imposition_url,
  filenames,
  target,
}: {
  imposition_url: string
  target: string
  filenames: Record<string, Promise<VirtualFile>>
}): Promise<VirtualFile> => {
  const data = await get_imposition_dispatch_data(imposition_url)
  const [action, ...args] = data.actions[target]

  if (!action) {
    throw `Invalid target ${target}, available targets are: ${keys(data.actions)}`
  }

  const func = actions[action]

  const task_id = data.imposition_id
    ? await AtomHash([data.imposition_id, target])
    : UUID()

  return func(task_id, data, filenames || {}, ...args)
}
